import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, SlidersHorizontal, Info } from 'lucide-react'
import { labelCls, inputCls } from '../styles'
import { DEFAULT_KYB_AU_CHECKS, DEFAULT_KYB_BLOCKING_DEFINITIONS, DEFAULT_KYB_OWNERSHIP, DEFAULT_KYB_TRUST_SPLIT } from '../data'
import type { KybCheckConfig, KybOwnershipConfig, KybTrustSplitConfig } from '../types'
import { applyIdSuggestions, useAiSeed } from '../aiConfigure'
import type { AiIdSuggestion, AiSeed } from '../aiConfigure'

export interface KybDiagramConfig {
  auChecks: KybCheckConfig[]
  ownership: KybOwnershipConfig
}

export interface KybAiSuggestion {
  auChecks: AiIdSuggestion[]
  ownership?: Partial<KybOwnershipConfig>
}

interface KybConfigPanelProps {
  onDiagramConfigChange: (config: KybDiagramConfig) => void
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<KybAiSuggestion> | null
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer shrink-0">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
      <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: checked ? '#6366f1' : '#e5e7eb' }} />
      <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
    </label>
  )
}

function SubSection({ title, description, children, defaultOpen = false }: { title: string; description: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[#1e293b]">{title}</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{description}</p>
        </div>
      </button>
      {open && <div className="p-3 space-y-3" style={{ borderTop: '1px solid #e5e7eb' }}>{children}</div>}
    </div>
  )
}

export function KybConfigPanel({ onDiagramConfigChange, aiSeed }: KybConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [auChecks, setAuChecks] = useState<KybCheckConfig[]>(DEFAULT_KYB_AU_CHECKS)
  const [ownership, setOwnership] = useState<KybOwnershipConfig>(DEFAULT_KYB_OWNERSHIP)

  useAiSeed(aiSeed, (suggestion) => {
    setAuChecks((prev) => applyIdSuggestions(prev, suggestion.auChecks))
    if (suggestion.ownership) setOwnership((prev) => ({ ...prev, ...suggestion.ownership }))
  })
  const [blockingDefs, setBlockingDefs] = useState<KybCheckConfig[]>(DEFAULT_KYB_BLOCKING_DEFINITIONS)
  const [kycForIndividuals, setKycForIndividuals] = useState(true)
  const [amlEntityScreening, setAmlEntityScreening] = useState(true)
  const [ongoingMonitoring, setOngoingMonitoring] = useState(false)
  const [trustSplit, setTrustSplit] = useState<KybTrustSplitConfig>(DEFAULT_KYB_TRUST_SPLIT)
  const [onboardingFormEnabled, setOnboardingFormEnabled] = useState(false)

  useEffect(() => {
    onDiagramConfigChange({ auChecks, ownership })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auChecks, ownership])

  function toggleAuCheck(id: string) {
    setAuChecks((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)))
  }

  function toggleBlockingDef(id: string) {
    setBlockingDefs((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)))
  }

  function setAllBlockingDefs(enabled: boolean) {
    setBlockingDefs((prev) => prev.map((c) => ({ ...c, enabled })))
  }

  const enabledBlockingCount = blockingDefs.filter((c) => c.enabled).length

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          KYB Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569]">
            Modelled on Operations Center&apos;s KYB tab (Ownership, Risk Model, Countries, Onboarding Form, Trust Analyzer) — that tab is still flagged &quot;Under Development&quot; in the real product, so this is a simplified stand-in rather than a literal 1:1 copy.
          </p>

          <SubSection title="Australian Business Checks" description="Checks run during onboarding for AU-registered businesses" defaultOpen>
            <div className="space-y-1.5">
              {auChecks.map((check) => (
                <div
                  key={check.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]"
                  style={check.enabled ? { background: '#f8fafc', border: '1px solid #e5e7eb' } : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.55 }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#1e293b]">{check.label}</p>
                    <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{check.description}</p>
                  </div>
                  <Toggle checked={check.enabled} onChange={() => toggleAuCheck(check.id)} />
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Ownership (UBO thresholds & blocking)" description="Mirrors Operations Center's Ownership Insights tab" defaultOpen>
            <div className="rounded-[9px] p-3" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <p className="text-[10px] text-[#475569] mb-2">Note: the UBO threshold writes to both kyb.json and srv_kyb simultaneously.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>UBO Threshold (%)</label>
                  <input
                    type="number" min={0} max={100} className={inputCls}
                    value={ownership.uboThresholdPercent}
                    onChange={(e) => setOwnership((o) => ({ ...o, uboThresholdPercent: Number(e.target.value) || 0 }))}
                  />
                  <p className="text-[10px] text-[#475569] mt-1">Ownership % at or above which a non-blocking individual is classified as a UBO.</p>
                </div>
                <div>
                  <label className={labelCls}>Blocking Threshold (%)</label>
                  <input
                    type="number" min={0} max={100} className={inputCls}
                    value={ownership.blockingThresholdPercent}
                    onChange={(e) => setOwnership((o) => ({ ...o, blockingThresholdPercent: Number(e.target.value) || 0 }))}
                  />
                  <p className="text-[10px] text-[#475569] mt-1">Must be 0 or equal to the UBO Threshold.</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls}>Blocking Definitions · {enabledBlockingCount} of {blockingDefs.length} codes active</label>
                <div className="flex gap-2">
                  <button onClick={() => setAllBlockingDefs(true)} className="text-[10px] text-[#4f46e5] hover:text-[#4f46e5]">ALL ON</button>
                  <button onClick={() => setAllBlockingDefs(false)} className="text-[10px] text-[#64748b] hover:text-[#475569]">ALL OFF</button>
                </div>
              </div>
              <div className="space-y-1.5">
                {blockingDefs.map((def) => (
                  <label key={def.id} className="flex items-start gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <input type="checkbox" checked={def.enabled} onChange={() => toggleBlockingDef(def.id)} className="mt-0.5" style={{ accentColor: '#6366f1' }} />
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-[#1e293b]">{def.label}</p>
                      <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{def.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </SubSection>

          <SubSection title="KYC for Individuals & UBO Consent" description="Whether identified directors/shareholders/UBOs are run through KYC">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Run KYC on individuals identified via KYB</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Directors, shareholders and UBOs get identity-verified once KYB resolves them — requires their explicit consent.</p>
              </div>
              <Toggle checked={kycForIndividuals} onChange={() => setKycForIndividuals((v) => !v)} />
            </div>
          </SubSection>

          <SubSection title="AML — Business Entity Screening" description="Sanctions & adverse media screening for the business entity itself, plus ongoing monitoring">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Screen business entity (sanctions + adverse media)</p>
              </div>
              <Toggle checked={amlEntityScreening} onChange={() => setAmlEntityScreening((v) => !v)} />
            </div>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Ongoing KYB monitoring (CreditorWatch alerts)</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Re-screens ownership, ASIC/ABR status, insolvency and credit events daily.</p>
              </div>
              <Toggle checked={ongoingMonitoring} onChange={() => setOngoingMonitoring((v) => !v)} />
            </div>
          </SubSection>

          <SubSection title="Trust Analyser" description="Trust deed analysis for trustees, beneficiaries and appointors">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Enable Trust Analyser</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Covers Unit Trusts, Discretionary Trusts and SMSFs — everything else falls under Other.</p>
              </div>
              <Toggle checked={trustSplit.enabled} onChange={() => setTrustSplit((t) => ({ ...t, enabled: !t.enabled }))} />
            </div>
            {trustSplit.enabled && (
              <div className="grid grid-cols-4 gap-2">
                {([
                  ['unitPercent', 'Unit %'],
                  ['discretionaryPercent', 'Discretionary %'],
                  ['smsfPercent', 'SMSF %'],
                  ['otherPercent', 'Other %'],
                ] as const).map(([key, label]) => (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <input
                      type="number" min={0} max={100} className={inputCls}
                      value={trustSplit[key]}
                      onChange={(e) => setTrustSplit((t) => ({ ...t, [key]: Number(e.target.value) || 0 }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </SubSection>

          <SubSection title="Onboarding Form" description="FrankieOne's hosted, white-label form for collecting business details">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Use FrankieOne&apos;s hosted KYB onboarding form</p>
              </div>
              <Toggle checked={onboardingFormEnabled} onChange={() => setOnboardingFormEnabled((v) => !v)} />
            </div>
          </SubSection>

          <p className="text-[10px] text-[#475569] flex items-start gap-1.5">
            <Info size={11} className="shrink-0 mt-0.5" />
            Countries &amp; International KYB are configured per-jurisdiction in the real product (Data Retrieval / Registry Documents / UBO Verify per country) — omitted here since this demo only exercises AU businesses.
          </p>
        </div>
      )}
    </div>
  )
}
